// =====================================================
// COMPONENTE LIGHTBOX PARA GERENCIAR COMISSÕES
// =====================================================

import React, { useState, useEffect } from 'react'
import {
    X,
    DollarSign,
    Calendar,
    User,
    Home,
    FileText,
    Save,
    AlertCircle,
    CheckCircle,
    Clock,
    TrendingUp,
    Trash2
} from 'lucide-react'
import { createClient } from '@supabase/supabase-js'
import { formatCurrency } from '../utils/formatCurrency'
import { CurrencyInputV2 } from './CurrencyInputV2'
import { useModal } from '../contexts/ModalContext'

// Configuração do Supabase
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY
const supabase = createClient(supabaseUrl, supabaseKey)

interface Venda {
    id: string
    imovel_id: string
    unidade: string
    corretor_id: string
    cliente_nome: string
    valor_venda: number
    data_venda: string
    valor_comissao: number
    valor_bonus: number
    previsao_recebimento: string
    observacoes: string
    created_at: string
    imovel?: {
        nome: string
        endereco: string
        valor_venda: number
        tipo_imovel: string
        construtora_id?: string
        proprietario_id?: string
    }
    corretor?: {
        nome: string
        email: string
        telefone?: string
        creci?: string
    }
}

interface Comissao {
    id: string
    venda_id: string
    valor_comissao: number
    valor_bonus: number
    data_pagamento?: string
    status: 'pendente' | 'pago' | 'cancelado'
    observacoes?: string
    created_at: string
    updated_at: string
}

interface ComissoesLightboxProps {
    venda: Venda | null
    isOpen: boolean
    onClose: () => void
}

export function ComissoesLightbox({ venda, isOpen, onClose }: ComissoesLightboxProps) {
    const [comissoes, setComissoes] = useState<Comissao[]>([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')
    const [isEditing, setIsEditing] = useState(false)
    const [editingComissao, setEditingComissao] = useState<Comissao | null>(null)
    const [isScrolled, setIsScrolled] = useState(false)

    // Form data para nova comissão
    const [formData, setFormData] = useState({
        valor_comissao: '',
        valor_bonus: '',
        data_pagamento: '',
        status: 'pendente' as 'pendente' | 'pago' | 'cancelado',
        observacoes: ''
    })

    // Estado para controlar o tipo de registro (comissão ou bônus)
    const [tipoRegistro, setTipoRegistro] = useState<'comissao' | 'bonus'>('comissao')
    const { setIsModalOpen: setGlobalModalOpen } = useModal()

    // Carregar comissões quando a venda muda
    useEffect(() => {
        if (venda && isOpen) {
            loadComissoes()
        }
    }, [venda, isOpen])

    // Função para detectar scroll
    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const target = e.target as HTMLDivElement
        setIsScrolled(target.scrollTop > 0)
    }

    // Função para calcular valor pago de COMISSÕES (apenas comissões)
    const calcularValorPagoComissoes = () => {
        return comissoes.reduce((total, comissao) => {
            return total + (comissao.valor_comissao || 0)
        }, 0)
    }

    // Função para calcular valor pago de BÔNUS (apenas bônus)
    const calcularValorPagoBonus = () => {
        return comissoes.reduce((total, comissao) => {
            return total + (comissao.valor_bonus || 0)
        }, 0)
    }

    // Função para calcular saldo restante de COMISSÕES
    const calcularSaldoRestanteComissoes = () => {
        const valorTotalComissao = venda?.valor_comissao || 0
        const valorPagoComissoes = calcularValorPagoComissoes()
        return valorTotalComissao - valorPagoComissoes
    }

    // Função para calcular saldo restante de BÔNUS
    const calcularSaldoRestanteBonus = () => {
        const valorTotalBonus = venda?.valor_bonus || 0
        const valorPagoBonus = calcularValorPagoBonus()
        return valorTotalBonus - valorPagoBonus
    }

    const loadComissoes = async () => {
        if (!venda) return

        setLoading(true)
        setError('')
        try {
            console.log('🔄 Carregando comissões para venda:', venda.id)

            const { data, error } = await supabase.rpc('listar_comissoes_venda', {
                p_venda_id: venda.id
            })

            console.log('📊 Resultado da query comissões:', { data, error })

            if (error) {
                console.error('❌ Erro na query comissões:', error)
                throw error
            }

            console.log('✅ Comissões carregadas com sucesso:', data?.length || 0, 'registros')
            setComissoes(data || [])
        } catch (err) {
            console.error('❌ Erro ao carregar comissões:', err)

            // Tratamento de erro mais detalhado
            let errorMessage = 'Erro desconhecido'

            if (err instanceof Error) {
                errorMessage = err.message
            } else if (typeof err === 'object' && err !== null) {
                if ('message' in err) {
                    errorMessage = String(err.message)
                } else if ('error' in err) {
                    errorMessage = String(err.error)
                }
            }

            console.error('❌ Detalhes do erro de carregamento:', {
                error: err,
                message: errorMessage,
                vendaId: venda?.id
            })

            setError(`Erro ao carregar comissões: ${errorMessage}`)
        } finally {
            setLoading(false)
        }
    }

    const handleSaveComissao = async () => {
        if (!venda) return

        // Validações baseadas no tipo de registro
        const valorComissao = tipoRegistro === 'comissao' ? formData.valor_comissao : '0'
        const valorBonus = tipoRegistro === 'bonus' ? formData.valor_bonus : '0'

        if (tipoRegistro === 'comissao' && (!formData.valor_comissao || parseFloat(formData.valor_comissao) <= 0)) {
            setError('Valor da comissão deve ser maior que zero')
            return
        }

        if (tipoRegistro === 'bonus' && (!formData.valor_bonus || parseFloat(formData.valor_bonus) <= 0)) {
            setError('Valor do bônus deve ser maior que zero')
            return
        }

        setLoading(true)
        setError('')
        try {
            console.log('💾 Salvando comissão:', formData)

            if (isEditing && editingComissao) {
                // Atualizar comissão existente
                const { data, error } = await supabase.rpc('atualizar_comissao_segura', {
                    p_id: editingComissao.id,
                    p_valor_comissao: parseFloat(valorComissao),
                    p_valor_bonus: parseFloat(valorBonus),
                    p_data_pagamento: formData.data_pagamento || null,
                    p_status: formData.status,
                    p_observacoes: formData.observacoes || null
                })

                if (error) {
                    console.error('❌ Erro ao atualizar comissão:', error)
                    throw error
                }

                if (!data) {
                    throw new Error('Falha ao atualizar comissão')
                }

                console.log('✅ Comissão atualizada com sucesso')
            } else {
                // Criar nova comissão
                const { data, error } = await supabase.rpc('criar_comissao_segura', {
                    p_venda_id: venda.id,
                    p_valor_comissao: parseFloat(valorComissao),
                    p_valor_bonus: parseFloat(valorBonus),
                    p_data_pagamento: formData.data_pagamento || null,
                    p_status: formData.status,
                    p_observacoes: formData.observacoes || null
                })

                if (error) {
                    console.error('❌ Erro ao criar comissão:', error)
                    throw error
                }

                if (!data) {
                    throw new Error('Falha ao criar comissão')
                }

                console.log('✅ Comissão criada com sucesso:', data)
            }

            // Recarregar comissões
            await loadComissoes()
            resetForm()
        } catch (err) {
            console.error('❌ Erro ao salvar comissão:', err)

            // Tratamento de erro mais detalhado
            let errorMessage = 'Erro desconhecido'

            if (err instanceof Error) {
                errorMessage = err.message
            } else if (typeof err === 'object' && err !== null) {
                // Se for um objeto de erro do Supabase
                if ('message' in err) {
                    errorMessage = String(err.message)
                } else if ('error' in err) {
                    errorMessage = String(err.error)
                }
            }

            console.error('❌ Detalhes do erro:', {
                error: err,
                message: errorMessage,
                formData: formData,
                isEditing: isEditing,
                editingComissao: editingComissao
            })

            setError(`Erro ao salvar comissão: ${errorMessage}`)
        } finally {
            setLoading(false)
        }
    }

    const handleEditComissao = (comissao: Comissao) => {
        setEditingComissao(comissao)
        setFormData({
            valor_comissao: comissao.valor_comissao.toString(),
            valor_bonus: comissao.valor_bonus.toString(),
            data_pagamento: comissao.data_pagamento || '',
            status: comissao.status,
            observacoes: comissao.observacoes || ''
        })
        // Determinar o tipo baseado nos valores existentes
        if (comissao.valor_comissao > 0 && comissao.valor_bonus === 0) {
            setTipoRegistro('comissao')
        } else if (comissao.valor_bonus > 0 && comissao.valor_comissao === 0) {
            setTipoRegistro('bonus')
        } else {
            // Se ambos têm valores, usar comissão como padrão
            setTipoRegistro('comissao')
        }
        setIsEditing(true)
        setGlobalModalOpen(true)
    }

    const resetForm = () => {
        setFormData({
            valor_comissao: '',
            valor_bonus: '',
            data_pagamento: '',
            status: 'pendente',
            observacoes: ''
        })
        setTipoRegistro('comissao')
        setIsEditing(false)
        setGlobalModalOpen(false)
        setEditingComissao(null)
    }

    const handleDeleteComissao = async (comissaoId: string) => {
        if (!confirm('Tem certeza que deseja excluir esta comissão?')) {
            return
        }

        setLoading(true)
        setError('')
        try {
            console.log('🗑️ Deletando comissão:', comissaoId)

            // Primeiro, tentar usar a função RPC
            try {
                const { data, error } = await supabase.rpc('deletar_comissao_segura', {
                    p_id: comissaoId
                })

                if (error) {
                    console.error('❌ Erro na função RPC:', error)
                    throw error
                }

                if (!data) {
                    throw new Error('Falha ao deletar comissão via RPC')
                }

                console.log('✅ Comissão deletada com sucesso via RPC')
            } catch (rpcError) {
                console.warn('⚠️ RPC falhou, tentando DELETE direto:', rpcError)

                // Fallback: usar DELETE direto na tabela
                const { error: deleteError } = await supabase
                    .from('comissoes')
                    .delete()
                    .eq('id', comissaoId)

                if (deleteError) {
                    console.error('❌ Erro no DELETE direto:', deleteError)
                    throw deleteError
                }

                console.log('✅ Comissão deletada com sucesso via DELETE direto')
            }

            // Recarregar comissões
            await loadComissoes()
        } catch (err) {
            console.error('❌ Erro ao deletar comissão:', err)

            // Tratamento de erro mais detalhado
            let errorMessage = 'Erro desconhecido'

            if (err instanceof Error) {
                errorMessage = err.message
            } else if (typeof err === 'object' && err !== null) {
                if ('message' in err) {
                    errorMessage = String(err.message)
                } else if ('error' in err) {
                    errorMessage = String(err.error)
                } else if ('details' in err) {
                    errorMessage = String(err.details)
                }
            }

            console.error('❌ Detalhes do erro de exclusão:', {
                error: err,
                message: errorMessage,
                comissaoId: comissaoId
            })

            setError(`Erro ao deletar comissão: ${errorMessage}`)
        } finally {
            setLoading(false)
        }
    }

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'pago':
                return <CheckCircle className="w-4 h-4 text-green-500" />
            case 'pendente':
                return <Clock className="w-4 h-4 text-yellow-500" />
            case 'cancelado':
                return <X className="w-4 h-4 text-red-500" />
            default:
                return <Clock className="w-4 h-4 text-gray-500" />
        }
    }

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'pago':
                return 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400'
            case 'pendente':
                return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400'
            case 'cancelado':
                return 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400'
            default:
                return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
        }
    }

    if (!isOpen || !venda) return null

    return (
        <>
            {/* Overlay */}
            <div
                className={`fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300 ${isOpen ? 'overlay-enter' : 'overlay-exit pointer-events-none'
                    }`}
                onClick={onClose}
            />

            {/* Lightbox */}
            <div
                className={`fixed top-0 right-0 h-full w-full max-w-2xl bg-white dark:bg-gray-800 shadow-2xl z-50 transform transition-transform duration-300 ease-in-out comissoes-lightbox ${isOpen ? 'lightbox-enter' : 'lightbox-exit'
                    }`}
            >
                {/* Header - Fixo no topo */}
                <div className="lightbox-header flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700">
                    <div className="flex items-center space-x-3">
                        <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                            <DollarSign className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div>
                            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                                Gerenciar Comissões
                            </h2>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                Venda: {venda.imovel?.nome || 'N/A'}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                    >
                        <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                    </button>
                </div>

                {/* Content - Área com scroll */}
                <div
                    className={`lightbox-content ${isScrolled ? 'scrolled' : ''}`}
                    onScroll={handleScroll}
                >
                    {/* Venda Info */}
                    <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Informações da Venda</h3>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-3">
                                <div className="flex items-center space-x-2">
                                    <Home className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                                    <span className="text-sm text-gray-600 dark:text-gray-400">Imóvel:</span>
                                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{venda.imovel?.nome || 'N/A'}</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <User className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                                    <span className="text-sm text-gray-600 dark:text-gray-400">Cliente:</span>
                                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{venda.cliente_nome}</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <DollarSign className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                                    <span className="text-sm text-gray-600 dark:text-gray-400">Valor:</span>
                                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{formatCurrency(venda.valor_venda)}</span>
                                </div>
                            </div>
                            <div className="space-y-3">
                                <div className="flex items-center space-x-2">
                                    <Calendar className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                                    <span className="text-sm text-gray-600 dark:text-gray-400">Data:</span>
                                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                        {new Date(venda.data_venda).toLocaleDateString('pt-BR')}
                                    </span>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <TrendingUp className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                                    <span className="text-sm text-gray-600 dark:text-gray-400">Comissão:</span>
                                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{formatCurrency(venda.valor_comissao)}</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <FileText className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                                    <span className="text-sm text-gray-600 dark:text-gray-400">Unidade:</span>
                                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{venda.unidade}</span>
                                </div>
                            </div>
                        </div>

                        {/* Valores de Comissão - Nova seção */}
                        <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                            <h4 className="text-md font-medium text-gray-900 dark:text-gray-100 mb-3">Valores de Comissão</h4>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                                    <div className="flex items-center space-x-2 mb-1">
                                        <TrendingUp className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                                        <span className="text-sm text-gray-600 dark:text-gray-400">Valor Total:</span>
                                    </div>
                                    <span className="text-lg font-bold text-gray-900 dark:text-gray-100">
                                        {formatCurrency(venda.valor_comissao)}
                                    </span>
                                </div>
                                <div className="bg-green-50 dark:bg-green-900/30 p-3 rounded-lg">
                                    <div className="flex items-center space-x-2 mb-1">
                                        <DollarSign className="w-4 h-4 text-green-600 dark:text-green-400" />
                                        <span className="text-sm text-gray-600 dark:text-gray-400">Valor Pago:</span>
                                    </div>
                                    <span className="text-lg font-bold text-green-600 dark:text-green-400">
                                        {formatCurrency(calcularValorPagoComissoes())}
                                    </span>
                                </div>
                                <div className={`p-3 rounded-lg ${calcularSaldoRestanteComissoes() > 0 ? 'bg-orange-50 dark:bg-orange-900/30' : 'bg-blue-50 dark:bg-blue-900/30'}`}>
                                    <div className="flex items-center space-x-2 mb-1">
                                        <AlertCircle className={`w-4 h-4 ${calcularSaldoRestanteComissoes() > 0 ? 'text-orange-600 dark:text-orange-400' : 'text-blue-600 dark:text-blue-400'}`} />
                                        <span className="text-sm text-gray-600 dark:text-gray-400">Saldo Restante:</span>
                                    </div>
                                    <span className={`text-lg font-bold ${calcularSaldoRestanteComissoes() > 0 ? 'text-orange-600 dark:text-orange-400' : 'text-blue-600 dark:text-blue-400'}`}>
                                        {formatCurrency(calcularSaldoRestanteComissoes())}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Valores de Bônus - Segunda seção */}
                        <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                            <h4 className="text-md font-medium text-gray-900 dark:text-gray-100 mb-3">Valores de Bônus</h4>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="bg-blue-50 dark:bg-blue-900/30 p-3 rounded-lg">
                                    <div className="flex items-center space-x-2 mb-1">
                                        <TrendingUp className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                        <span className="text-sm text-blue-600 dark:text-blue-400">Valor Total:</span>
                                    </div>
                                    <span className="text-lg font-bold text-blue-900 dark:text-blue-100">
                                        {formatCurrency(venda.valor_bonus)}
                                    </span>
                                </div>
                                <div className="bg-green-50 dark:bg-green-900/30 p-3 rounded-lg">
                                    <div className="flex items-center space-x-2 mb-1">
                                        <DollarSign className="w-4 h-4 text-green-600 dark:text-green-400" />
                                        <span className="text-sm text-gray-600 dark:text-gray-400">Valor Pago:</span>
                                    </div>
                                    <span className="text-lg font-bold text-green-600 dark:text-green-400">
                                        {formatCurrency(calcularValorPagoBonus())}
                                    </span>
                                </div>
                                <div className={`p-3 rounded-lg ${calcularSaldoRestanteBonus() > 0 ? 'bg-orange-50 dark:bg-orange-900/30' : 'bg-blue-50 dark:bg-blue-900/30'}`}>
                                    <div className="flex items-center space-x-2 mb-1">
                                        <AlertCircle className={`w-4 h-4 ${calcularSaldoRestanteBonus() > 0 ? 'text-orange-600 dark:text-orange-400' : 'text-blue-600 dark:text-blue-400'}`} />
                                        <span className="text-sm text-gray-600 dark:text-gray-400">Saldo Restante:</span>
                                    </div>
                                    <span className={`text-lg font-bold ${calcularSaldoRestanteBonus() > 0 ? 'text-orange-600 dark:text-orange-400' : 'text-blue-600 dark:text-blue-400'}`}>
                                        {formatCurrency(calcularSaldoRestanteBonus())}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Total a Receber desta venda */}
                        <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-3">
                                        <div className="p-2 bg-blue-100 dark:bg-blue-800/50 rounded-lg">
                                            <DollarSign className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                                        </div>
                                        <div>
                                            <h4 className="text-lg font-semibold text-blue-900 dark:text-blue-100">Total a Receber desta venda</h4>
                                            <p className="text-sm text-blue-600 dark:text-blue-400">Saldo restante de comissão + bônus</p>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-2xl font-bold text-blue-900 dark:text-blue-100">
                                            {formatCurrency(calcularSaldoRestanteComissoes() + calcularSaldoRestanteBonus())}
                                        </p>
                                        <p className="text-sm text-blue-600 dark:text-blue-400">
                                            {calcularSaldoRestanteComissoes() + calcularSaldoRestanteBonus() > 0 ? 'Pendente' : 'Quitado'}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Comissões List */}
                    <div className="p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Comissões</h3>
                            <button
                                onClick={resetForm}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                            >
                                Nova {tipoRegistro === 'comissao' ? 'Comissão' : 'Bônus'}
                            </button>
                        </div>

                        {/* Mensagem de Erro */}
                        {error && (
                            <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center space-x-2">
                                <AlertCircle className="w-5 h-5 text-red-500 dark:text-red-400" />
                                <span className="text-red-700 dark:text-red-400 text-sm">{error}</span>
                            </div>
                        )}

                        {loading ? (
                            <div className="flex items-center justify-center py-8">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {comissoes.map((comissao) => (
                                    <div
                                        key={comissao.id}
                                        className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors comissao-card comissao-item"
                                    >
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center space-x-3">
                                                {getStatusIcon(comissao.status)}
                                                <div>
                                                    <div className="space-y-1">
                                                        {comissao.valor_comissao > 0 && (
                                                            <div className="flex items-center space-x-2">
                                                                <span className="font-medium text-gray-900 dark:text-gray-100">
                                                                    {formatCurrency(comissao.valor_comissao)}
                                                                </span>
                                                                <span className="text-sm text-gray-600 dark:text-gray-400">
                                                                    Comissão
                                                                </span>
                                                            </div>
                                                        )}
                                                        {comissao.valor_bonus > 0 && (
                                                            <div className="flex items-center space-x-2">
                                                                <span className="font-medium text-gray-900 dark:text-gray-100">
                                                                    {formatCurrency(comissao.valor_bonus)}
                                                                </span>
                                                                <span className="text-sm text-gray-600 dark:text-gray-400">
                                                                    bônus
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                                        {comissao.data_pagamento &&
                                                            `Pagamento: ${new Date(comissao.data_pagamento).toLocaleDateString('pt-BR')}`
                                                        }
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(comissao.status)}`}>
                                                    {comissao.status}
                                                </span>
                                                <button
                                                    onClick={() => handleEditComissao(comissao)}
                                                    className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors action-button"
                                                    title="Editar comissão"
                                                >
                                                    <FileText className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteComissao(comissao.id)}
                                                    className="p-1 hover:bg-red-100 dark:hover:bg-red-900/30 rounded transition-colors action-button"
                                                    title="Excluir comissão"
                                                >
                                                    <Trash2 className="w-4 h-4 text-red-400 dark:text-red-500" />
                                                </button>
                                            </div>
                                        </div>
                                        {comissao.observacoes && (
                                            <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                                                {comissao.observacoes}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Form */}
                    <div className="p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700">
                        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                            {isEditing
                                ? `Editar ${tipoRegistro === 'comissao' ? 'Comissão' : 'Bônus'}`
                                : `Nova ${tipoRegistro === 'comissao' ? 'Comissão' : 'Bônus'}`
                            }
                        </h3>

                        <div className="space-y-4">
                            {/* Seleção do tipo de registro */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                                    Tipo de Registro *
                                </label>
                                <div className="flex gap-6">
                                    <label className="flex items-center">
                                        <input
                                            type="radio"
                                            name="tipoRegistro"
                                            value="comissao"
                                            checked={tipoRegistro === 'comissao'}
                                            onChange={(e) => setTipoRegistro(e.target.value as 'comissao' | 'bonus')}
                                            className="w-4 h-4 text-blue-600 border-gray-300 dark:border-gray-600 focus:ring-blue-500 dark:bg-gray-700"
                                        />
                                        <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Comissão</span>
                                    </label>
                                    <label className="flex items-center">
                                        <input
                                            type="radio"
                                            name="tipoRegistro"
                                            value="bonus"
                                            checked={tipoRegistro === 'bonus'}
                                            onChange={(e) => setTipoRegistro(e.target.value as 'comissao' | 'bonus')}
                                            className="w-4 h-4 text-blue-600 border-gray-300 dark:border-gray-600 focus:ring-blue-500 dark:bg-gray-700"
                                        />
                                        <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Bônus</span>
                                    </label>
                                </div>
                            </div>

                            {/* Campo de valor baseado no tipo selecionado */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    {tipoRegistro === 'comissao' ? 'Valor da Comissão' : 'Valor do Bônus'} *
                                </label>
                                <CurrencyInputV2
                                    value={tipoRegistro === 'comissao' ? formData.valor_comissao : formData.valor_bonus}
                                    onChange={(value) => setFormData({
                                        ...formData,
                                        [tipoRegistro === 'comissao' ? 'valor_comissao' : 'valor_bonus']: value
                                    })}
                                    placeholder="R$ 0,00"
                                    className="currency-input"
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Data de Pagamento
                                    </label>
                                    <input
                                        type="date"
                                        value={formData.data_pagamento}
                                        onChange={(e) => setFormData({ ...formData, data_pagamento: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Status
                                    </label>
                                    <select
                                        value={formData.status}
                                        onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                    >
                                        <option value="pendente">Pendente</option>
                                        <option value="pago">Pago</option>
                                        <option value="cancelado">Cancelado</option>
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Observações
                                </label>
                                <textarea
                                    value={formData.observacoes}
                                    onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                                    rows={3}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
                                    placeholder="Observações sobre a comissão..."
                                />
                            </div>

                            <div className="flex items-center justify-end space-x-3">
                                <button
                                    onClick={resetForm}
                                    className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-600 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={handleSaveComissao}
                                    disabled={loading}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                                >
                                    <Save className="w-4 h-4" />
                                    <span>{loading ? 'Salvando...' : `Salvar ${tipoRegistro === 'comissao' ? 'Comissão' : 'Bônus'}`}</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}
